/* istanbul ignore file */
import { FC, useEffect, useLayoutEffect, useMemo } from 'react';
import { useBrand } from 'hooks/useBrand';

import { ErrorDisplay } from 'components/fragments/ErrorDisplay';
import { ErrorState } from 'components/UI/ErrorState';
import { Loader } from 'components/UI/Loader';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { useTranslations } from 'hooks/useTranslations';
import { useQuery } from 'hooks/useQuery';
import { useGetTranslationsQuery } from 'store/diamApi';
import {
  fetchTranslation,
  localizationSelector,
  setLanguage
} from 'store/localizationState';
import { setBrand } from 'store/brandState/brandState';
import { setGroup } from 'store/groupState/groupState';
import { ThemeWrapper } from 'styles';
import { Router } from 'router';
import { PROTECTED_ROUTES } from 'router/routes';
import {
  BRAND_QUERY_PARAM,
  GROUP_QUERY_PARAM,
  LANGUAGE_QUERY_PARAM,
  KNOWN_QUERY_PARAMS
} from 'constants/queryParams';
import { removeQueryParamsFromUrl } from 'utils/urlUtils';
import { REDIRECT_AFTER_LOGIN_KEY } from 'utils/constants';

export const App: FC = () => {
  const t = useTranslations();
  const dispatch = useAppDispatch();
  const [language] = useQuery(LANGUAGE_QUERY_PARAM);
  const [brand] = useQuery(BRAND_QUERY_PARAM);
  const [group] = useQuery(GROUP_QUERY_PARAM);
  const { languageCode } = useAppSelector(localizationSelector);
  const { data, isLoading, isError } = useGetTranslationsQuery();
  const { brandConfig } = useBrand();

  // Determine and set brand
  useLayoutEffect(() => {
    dispatch(setBrand(brand));
  }, [dispatch]);

  // Store group from query param
  useLayoutEffect(() => {
    if (group) {
      dispatch(setGroup(group));
    }
  }, [dispatch]);

  // Determine and set language
  useEffect(() => {
    if (data) {
      dispatch(setLanguage(data, language));
    }
  }, [dispatch, language, data]);

  // Fetch translations
  useEffect(() => {
    if (languageCode) {
      dispatch(fetchTranslation(languageCode));
    }
  }, [dispatch, languageCode]);

  // Remove interpreted known query parameters from the URL
  useEffect(() => {
    removeQueryParamsFromUrl(KNOWN_QUERY_PARAMS);
  }, []);

  // IAM-4754: Save requested path to redirect to after sucessful login.
  useEffect(() => {
    const requestedPath = window.location.pathname;
    if (PROTECTED_ROUTES.includes(requestedPath)) {
      sessionStorage.setItem(REDIRECT_AFTER_LOGIN_KEY, requestedPath);
    }
  }, []);

  // Wait for translations
  const content = useMemo(() => {
    if (isLoading) {
      return <Loader />;
    }

    if (!data || isError) {
      return <ErrorState message={t('errors.unknown_error')} />;
    }

    return (
      <>
        <Router />
        <ErrorDisplay />
      </>
    );
  }, [t, data, isLoading, isError]);

  return <ThemeWrapper theme={brandConfig.theme}>{content}</ThemeWrapper>;
};
